import React, { useState } from "react";
import {
  Table,
  Button,
  Tag,
  Space,
  message,
  Typography,
  Switch,
  Spin,
  Form,
  Select,
  Input,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  SafetyCertificateOutlined,
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  FetchUsers_api,
  UpdateUserStatus_api,
  FetchAppFeatures_api,
  FetchUserPermissions_api,
  UpdateUserPermissions_api,
  FetchUserTypes_api,
  AddUser_api,
  UpdateUser_api,
} from "../../Api";
import { useQuery } from "@tanstack/react-query";
import { UserFormModal } from "./common/UserFormModal";
import { PermissionModal } from "./common/PermissionModal";
import { FeatureManagementModal } from "./common/FeatureManagementModal";
import { useAuthChecker } from "../../Context";

export const ManageUser = () => {
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<number[]>([]);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const { user } = useAuthChecker();

  // Pagination & Search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");

  const {
    data: usersData,
    refetch: refetchUsers,
    isLoading: isLoadingUsers,
  } = useQuery({
    queryKey: ["users", page, pageSize, searchText],
    queryFn: () => FetchUsers_api(page, pageSize, searchText),
  });

  const { data: userTypesData } = useQuery({
    queryKey: ["userTypes"],
    queryFn: FetchUserTypes_api,
  });

  const { data: featuresData, isLoading: isLoadingFeatures } = useQuery({
    queryKey: ["appFeatures"],
    queryFn: FetchAppFeatures_api,
  });

  const handleStatusChange = async (userId: string, currentStatus: string) => {
    setUpdatingStatusId(userId);
    const newStatus = currentStatus === "1" ? 0 : 1;
    const res = await UpdateUserStatus_api(userId, newStatus, user?.userId);
    if (res?.success) {
      message.success(res.msg);
      refetchUsers();
    }
    setUpdatingStatusId(null);
  };

  const handleOpenPermissions = async (user: any) => {
    setSelectedUser(user);
    const res = await FetchUserPermissions_api(user.id);
    if (res?.success) {
      setSelectedFeatures(res.feature_ids.map((id: string) => parseInt(id)));
      setIsPermissionModalOpen(true);
    }
  };

  const handleSavePermissions = async () => {
    setIsLoading(true);
    try {
      if (!selectedUser) return;
      const res = await UpdateUserPermissions_api(
        selectedUser.id,
        selectedFeatures,
        user?.userId,
      );
      if (res?.success) {
        message.success(res.msg);
        setIsPermissionModalOpen(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenUserModal = (user: any = null) => {
    setEditingUser(user);
    setIsUserModalOpen(true);
  };

  const handleSaveUser = async (values: any) => {
    try {
      const payload = {
        ...values,
        is_active: values.is_active ? 1 : 0,
        id: editingUser?.id,
        adminId: user?.userId,
      };

      const res = editingUser
        ? await UpdateUser_api(payload)
        : await AddUser_api(payload);

      if (res?.success) {
        message.success(res.msg);
        setIsUserModalOpen(false);
        refetchUsers();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const featuresByCategory = featuresData?.features?.reduce(
    (acc: any, feature: any) => {
      const category = feature.category_name;
      if (!acc[category]) acc[category] = [];
      acc[category].push(feature);
      return acc;
    },
    {},
  );

  const handleToggleFeature = (id: number) => {
    setSelectedFeatures((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id],
    );
  };

  const handleToggleCategory = (category: string, featureIds: number[]) => {
    setSelectedFeatures((prev) => {
      const allSelected = featureIds.every((id) => prev.includes(id));

      if (allSelected) {
        return prev.filter((id) => !featureIds.includes(id));
      }

      return Array.from(new Set([...prev, ...featureIds]));
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: "User",
      key: "user",
      width: 220,
      render: (record: any) => (
        <div>
          <div className="font-semibold text-gray-800">{record.username}</div>
          <div className="text-xs text-gray-500">ID: {record.id}</div>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "user_type_name",
      key: "user_type_name",
      width: 140,
      render: (type: string) => (
        <Tag color="blue" className="rounded-full px-3">
          {type}
        </Tag>
      ),
    },
    {
      title: "Last login",
      dataIndex: "lastlogin",
      key: "lastlogin",
      width: 160,
      render: (date: string) => (
        <span className="text-gray-600 text-sm">
          {date ? new Date(date).toLocaleDateString() : "-"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 140,
      align: "center",
      render: (record: any) => (
        <Switch
          checked={record.is_active === "1"}
          loading={updatingStatusId === record.id}
          onChange={() => handleStatusChange(record.id, record.is_active)}
          size="small"
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      align: "right",
      render: (record: any) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenUserModal(record)}
          />
          <Button
            size="small"
            type="primary"
            icon={<SafetyCertificateOutlined />}
            onClick={() => handleOpenPermissions(record)}
          />
        </Space>
      ),
    },
  ];

  console.log(isLoading);

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">User Management</h1>

        <div className="flex gap-4">
          <Input
            placeholder="Search users..."
            prefix={<SearchOutlined />}
            className="w-64"
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
          <Button
            icon={<PlusOutlined />}
            onClick={() => setIsFeatureModalOpen(true)}
            size="middle"
          >
            Manage Features
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenUserModal()}
            size="middle"
          >
            Add New User
          </Button>
        </div>
      </div>

      <div className="px-6 pb-6">
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <div className="p-4">
            <Table
              dataSource={usersData?.users || []}
              columns={columns}
              rowKey="id"
              loading={isLoadingUsers}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: usersData?.total || 0,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
                showSizeChanger: true,
              }}
              className="user-management-table overflow-x-auto"
            />
          </div>
        </div>
      </div>

      {/* User Add/Edit Modal */}
      <UserFormModal
        open={isUserModalOpen}
        editingUser={editingUser}
        userTypes={userTypesData?.user_types || []}
        onCancel={() => setIsUserModalOpen(false)}
        onSave={handleSaveUser}
      />

      {/* Permission Management Modal */}
      <PermissionModal
        open={isPermissionModalOpen}
        user={selectedUser}
        loading={isLoadingFeatures}
        featuresByCategory={featuresByCategory || {}}
        selectedFeatures={selectedFeatures}
        onToggleFeature={handleToggleFeature}
        onSave={handleSavePermissions}
        onCancel={() => setIsPermissionModalOpen(false)}
        onToggleCategory={handleToggleCategory}
        isLoading={isLoading}
      />

      {/* Feature Management Modal */}
      <FeatureManagementModal
        open={isFeatureModalOpen}
        onCancel={() => setIsFeatureModalOpen(false)}
      />
    </div>
  );
};
